import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Edit, Trash2, MapPin, FileSpreadsheet, Camera, Thermometer, Link2, MessageSquare, ChevronDown, CheckCheck, Send, Calculator } from 'lucide-react';
import PayrollPreview from '../components/PayrollPreview';
import Layout from '@/components/Layout';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Normalize photo URL: old entries have /media/, new ones have /api/media/
const fixPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/media/')) return url.replace('/media/', '/api/media/');
  return url;
};

import { createTranslator } from '@/i18n';
import { useLanguage } from '@/context/LanguageContext';

// 24h tímainnsláttur: "8" -> "08:00", "830" -> "08:30", "1645" -> "16:45"
const normalizeTime = (raw) => {
  const d = String(raw || '').replace(/[^\d]/g, '');
  if (!d) return '';
  let h, m;
  if (d.length <= 2) { h = parseInt(d, 10); m = 0; }
  else if (d.length === 3) { h = parseInt(d.slice(0, 1), 10); m = parseInt(d.slice(1), 10); }
  else { h = parseInt(d.slice(0, 2), 10); m = parseInt(d.slice(2, 4), 10); }
  if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toTimeStr = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// Reiknar vinnutíma; ef út-tími er <= inn-tíma er gert ráð fyrir næsta degi
const getDuration = (date, timeIn, timeOut) => {
  const tIn = normalizeTime(timeIn);
  const tOut = normalizeTime(timeOut);
  if (!date || !tIn || !tOut) return null;
  const a = new Date(`${date}T${tIn}:00`);
  const b = new Date(`${date}T${tOut}:00`);
  let nextDay = false;
  if (b <= a) { b.setDate(b.getDate() + 1); nextDay = true; }
  return { hours: (b - a) / 3600000, nextDay };
};

const TimeInput24 = ({ label, value, onChange, required, testId }) => {
  const [err, setErr] = useState(false);
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        value={value}
        placeholder="t.d. 08:00"
        inputMode="numeric"
        maxLength={5}
        className={`font-mono text-lg tracking-widest text-center ${err ? 'border-red-500' : ''}`}
        onChange={(e) => { onChange(e.target.value); setErr(false); }}
        onBlur={(e) => {
          const n = normalizeTime(e.target.value);
          if (n === null) setErr(true);
          else { onChange(n); setErr(false); }
        }}
        required={required}
        data-testid={testId}
      />
      {err && <p className="text-xs text-red-600">Ógildur tími – notaðu 24h snið (t.d. 16:30)</p>}
    </div>
  );
};

const DurationHint = ({ date, timeIn, timeOut }) => {
  const dur = getDuration(date, timeIn, timeOut);
  if (!dur) return null;
  return (
    <p className="text-sm font-medium text-blue-700 bg-blue-50 rounded-md px-3 py-2" data-testid="duration-hint">
      Vinnutími: {dur.hours.toFixed(2).replace('.', ',')} klst
      {dur.nextDay && ' (fer yfir miðnætti – út daginn eftir)'}
    </p>
  );
};

export default function TimeSheet({ user, onLogout }) {
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('active'); // active, inactive, all, or employee_id

  const { language } = useLanguage();
  const t = createTranslator(language || 'is');

  // Calculate default date range (26th of last month to 25th of current month)
  const getDefaultDateRange = () => {
    const today = new Date();
    const currentDay = today.getDate();

    let fromDate, toDate;

    if (currentDay >= 26) {
      // We're past the 26th, so range is 26th this month to 25th next month
      fromDate = new Date(today.getFullYear(), today.getMonth(), 26);
      toDate = new Date(today.getFullYear(), today.getMonth() + 1, 25);
    } else {
      // We're before the 26th, so range is 26th last month to 25th this month
      fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 26);
      toDate = new Date(today.getFullYear(), today.getMonth(), 25);
    }

    return {
      from: fromDate.toISOString().split('T')[0],
      to: toDate.toISOString().split('T')[0],
    };
  };

  const [dateFilter, setDateFilter] = useState(getDefaultDateRange());
  const [salaryPeriod, setSalaryPeriod] = useState(null);

  const shiftMonth = (dateStr, months) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const target = new Date(y, m - 1 + months, d);
    if (target.getDate() !== d) target.setDate(0);
    return toDateStr(target);
  };

  const currentPeriod = () => salaryPeriod || getDefaultDateRange();

  const previousPeriod = () => {
    const cur = currentPeriod();
    const [y, m, d] = cur.from.split('-').map(Number);
    const prevEnd = new Date(y, m - 1, d - 1);
    return { from: shiftMonth(cur.from, -1), to: toDateStr(prevEnd) };
  };

  const isPeriodActive = (period) => dateFilter.from === period.from && dateFilter.to === period.to;

  useEffect(() => {
    const loadSalaryPeriod = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.salary_period_start && res.data?.salary_period_end) {
          const period = { from: res.data.salary_period_start, to: res.data.salary_period_end };
          setSalaryPeriod(period);
          setDateFilter(period);
        }
      } catch { /* halda sjálfgefnu tímabili */ }
    };
    loadSalaryPeriod();
  }, []);
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | approved | rejected
  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [openMapEntryId, setOpenMapEntryId] = useState(null);
  const [editForm, setEditForm] = useState({
    date: '',
    time_in: '',
    time_out: '',
  });
  const [newEntryOpen, setNewEntryOpen] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [newEntryForm, setNewEntryForm] = useState({
    user_id: '',
    date: toDateStr(new Date()),
    time_in: '',
    time_out: '',
  });
  const [sickLeaves, setSickLeaves] = useState([]);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [photoModalUrl, setPhotoModalUrl] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkEmployee, setLinkEmployee] = useState('');
  const [commentEntry, setCommentEntry] = useState(null);
  const [sickOpen, setSickOpen] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/time/entries`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Validate response
      if (Array.isArray(response.data)) {
        const validEntries = response.data.filter(
          (entry) => entry && typeof entry === 'object' && entry.id,
        );
        setEntries(validEntries);
      } else {
        setEntries([]);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Villa við að sækja tímaskrár');
      setEntries([]);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/with-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Validate response is array
      if (Array.isArray(response.data)) {
        const validEmployees = response.data.filter(
          (emp) => emp && typeof emp === 'object' && emp.user_id && emp.full_name,
        );
        setEmployees(validEmployees);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  }, []);

  const fetchSickLeaves = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leave/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(response.data)) {
        setSickLeaves(
          response.data.filter(
            (r) => r.leave_type === 'sick' && r.status === 'approved',
          ),
        );
      }
    } catch (error) {
      console.error('Error fetching sick leaves:', error);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchEmployees();
    fetchSickLeaves();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchEntries();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchEntries, fetchEmployees, fetchSickLeaves]);

  const handleEdit = (entry) => {
    setEditEntry(entry);

    const inD = entry.clock_in ? new Date(entry.clock_in) : null;
    const outD = entry.clock_out ? new Date(entry.clock_out) : null;

    setEditForm({
      date: inD ? toDateStr(inD) : toDateStr(new Date()),
      time_in: inD ? toTimeStr(inD) : '',
      time_out: outD ? toTimeStr(outD) : '',
    });
    setEditOpen(true);
  };

  // Duplicate functions removed

  // Byggir clock_in/clock_out úr dagsetningu + 24h tímum; út <= inn = næsti dagur
  const buildTimes = (date, timeIn, timeOut, outRequired = false) => {
    const tIn = normalizeTime(timeIn);
    const tOut = normalizeTime(timeOut);
    if (!date || !tIn) {
      toast.error('Vantar dagsetningu eða inn-tíma (24h snið, t.d. 08:00)');
      return null;
    }
    if (tOut === null || (outRequired && !tOut)) {
      toast.error('Ógildur út-tími – notaðu 24h snið (t.d. 16:30)');
      return null;
    }
    const inDt = new Date(`${date}T${tIn}:00`);
    let outDt = null;
    if (tOut) {
      outDt = new Date(`${date}T${tOut}:00`);
      if (outDt <= inDt) outDt.setDate(outDt.getDate() + 1);
    }
    return { clock_in: inDt.toISOString(), clock_out: outDt ? outDt.toISOString() : null };
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    const times = buildTimes(editForm.date, editForm.time_in, editForm.time_out);
    if (!times) return;
    try {
      const token = localStorage.getItem('token');

      await axios.put(`${API}/time/entries/${editEntry.id}`, times, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Tímaskráning uppfærð!');
      setEditOpen(false);
      fetchEntries();
    } catch (error) {
      console.error('Update error:', error);

      // Handle validation errors
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMsg = error.response.data.detail
            .map((err) => `${err.loc?.join('.')}: ${err.msg}`)
            .join(', ');
          toast.error(errorMsg);
        } else if (typeof error.response.data.detail === 'string') {
          toast.error(error.response.data.detail);
        } else {
          toast.error('Villa í gögnum');
        }
      } else {
        toast.error('Uppfærsla mistókst');
      }
    }
  };

  const handleToggleSelect = (entryId) => {
    setSelectedEntries((prev) =>
      prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId],
    );
  };

  const handleApproveSelected = async () => {
    if (selectedEntries.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/time/entries/bulk-status`,
        { entry_ids: selectedEntries, status: 'approved' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Valdar tímaskráningar samþykktar');
      setSelectedEntries([]);
      fetchEntries();
    } catch (error) {
      console.error('Bulk approve error:', error);
      toast.error('Tókst ekki að samþykkja valdar færslur');
    }
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    const times = buildTimes(newEntryForm.date, newEntryForm.time_in, newEntryForm.time_out);
    if (!times) return;
    try {
      const token = localStorage.getItem('token');
      const payload = { ...times };

      // Admin má velja starfsmann
      if (user.role === 'admin' && newEntryForm.user_id) {
        payload.user_id = newEntryForm.user_id;
      }

      await axios.post(`${API}/time/manual`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Tímaskráning búin til');
      setNewEntryOpen(false);
      setNewEntryForm({ user_id: '', date: toDateStr(new Date()), time_in: '', time_out: '' });
      fetchEntries();
    } catch (error) {
      console.error('Create entry error:', error);
      toast.error('Tókst ekki að bjóða til tímaskráningu');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Ertu viss um að þú viljir eyða þessari tímaskráningu?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/time/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Tímaskráning eytt!');
      fetchEntries();
    } catch (error) {
      console.error('Delete error:', error);

      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Villa kom upp við að eyða');
      }
    }
  };

  const handleToggleMap = (entry) => {
    if (!entry.location_lat || !entry.location_lng) {
      return;
    }

    if (openMapEntryId === entry.id) {
      setOpenMapEntryId(null);
    } else {
      setOpenMapEntryId(entry.id);
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');

      const params = new URLSearchParams();
      if (dateFilter.from) params.append('from_date', dateFilter.from);
      if (dateFilter.to) params.append('to_date', dateFilter.to);

      const response = await axios.get(
        `${API}/time/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `timaskra_${new Date().toISOString().split('T')[0]}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Tímaskrá sótt í Excel!');
    } catch (error) {
      toast.error('Villa við að sækja Excel skrá');
    }
  };

  const handleSendToPayday = async () => {
    if (!dateFilter.from || !dateFilter.to) {
      toast.error('Veldu tímabil fyrst');
      return;
    }
    if (!window.confirm(`Senda tímaskrá fyrir tímabilið ${dateFilter.from} til ${dateFilter.to} í Payday?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/payday/upload-timesheet`,
        { from_date: dateFilter.from, to_date: dateFilter.to },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Tímaskrá send í Payday: ${res.data.employees} starfsmenn, ${res.data.items} launaliðir`);
      if (res.data.ssns_not_found?.length) {
        toast.warning(`Kennitölur ekki til í Payday: ${res.data.ssns_not_found.join(', ')} – samstilltu starfsmenn fyrst í Stillingum`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Villa við að senda tímaskrá í Payday');
    }
  };

  const handleGenerateLink = async () => {
    if (!linkEmployee) {
      toast.error('Veldu starfsmann');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/time/generate-review-link`,
        { user_id: linkEmployee, from_date: dateFilter.from || null, to_date: dateFilter.to || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const link = `${window.location.origin}/review/${res.data.token}`;
      setGeneratedLink({ url: link, name: res.data.user_name, expires: res.data.expires_at });
    } catch (error) {
      toast.error('Villa við að búa til link');
    }
  };

  const copyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink.url);
      toast.success('Linkur afritaður!');
    }
  };

  const getEmployeeName = (userId) => {
    const employee = employees.find((emp) => emp.user_id === userId);
    return employee ? employee.full_name : 'Óþekktur';
  };

  const getEmployeeStatus = (userId) => {
    const employee = employees.find((emp) => emp.user_id === userId);
    return employee ? employee.is_active : true;
  };

  const filteredEntries = entries.filter((entry) => {
    const isActive = getEmployeeStatus(entry.user_id);

    // Filter by employee status
    let employeeMatch = true;
    if (filter === 'active') employeeMatch = isActive === true;
    else if (filter === 'inactive') employeeMatch = isActive === false;
    else if (filter === 'all') employeeMatch = true;
    else employeeMatch = entry.user_id === filter; // specific employee

    if (!employeeMatch) return false;

    // Filter by entry status
    if (statusFilter !== 'all') {
      if ((entry.status || 'pending') !== statusFilter) return false;
    }

    // Filter by date range
    if (dateFilter.from || dateFilter.to) {
      const entryDate = new Date(entry.clock_in);

      if (dateFilter.from && dateFilter.to) {
        const fromDate = new Date(dateFilter.from);
        fromDate.setHours(0, 0, 0, 0);
        const toDate = new Date(dateFilter.to);
        toDate.setHours(23, 59, 59, 999);
        return entryDate >= fromDate && entryDate <= toDate;
      } else if (dateFilter.from) {
        const fromDate = new Date(dateFilter.from);
        fromDate.setHours(0, 0, 0, 0);
        return entryDate >= fromDate;
      } else if (dateFilter.to) {
        const toDate = new Date(dateFilter.to);
        toDate.setHours(23, 59, 59, 999);
        return entryDate <= toDate;
      }
    }

    return true;
  });

  // Get active employees for dropdown
  const activeEmployees = employees.filter((emp) => emp.is_active);
  const inactiveEmployees = employees.filter((emp) => !emp.is_active);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="timesheet-page">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold mb-1">{t('timesheet_title')}</h1>
          <div className="flex gap-2">
            <Button onClick={() => setNewEntryOpen(true)} data-testid="new-entry-button">
              {t('timesheet_new_entry')}
            </Button>
            <Button
              onClick={() => {
                fetchEntries();
                fetchEmployees();
                toast.success('Uppfært!');
              }}
              variant="outline"
              data-testid="refresh-button"
            >
              <Clock className="mr-2 h-4 w-4" />
              {t('timesheet_refresh')}
            </Button>
            <Button
              onClick={handleExportExcel}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              data-testid="export-excel-button"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              {t('timesheet_export_excel')}
            </Button>
            {user.role === 'admin' && (
              <Button
                onClick={handleSendToPayday}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                data-testid="send-to-payday-button"
              >
                <Send className="mr-2 h-4 w-4" />
                Senda í Payday
              </Button>
            )}
            {user.role === 'admin' && (
              <Button
                variant="outline"
                onClick={() => setPayrollOpen(true)}
                data-testid="payroll-preview-button"
              >
                <Calculator className="mr-2 h-4 w-4" />
                Launakeyrsla
              </Button>
            )}
            {user.role === 'admin' && (
              <Button
                variant="outline"
                onClick={() => { setGeneratedLink(null); setLinkEmployee(''); setLinkDialogOpen(true); }}
                data-testid="generate-link-button"
              >
                <Link2 className="mr-2 h-4 w-4" />
                Búa til link
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        {user.role === 'admin' && (
          <PayrollPreview
            open={payrollOpen}
            onOpenChange={setPayrollOpen}
            fromDate={dateFilter.from}
            toDate={dateFilter.to}
          />
        )}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-center">
              <Label className="font-semibold">{t('timesheet_filter_employees')}</Label>
              <Button
                variant={filter === 'active' ? 'default' : 'outline'}
                onClick={() => setFilter('active')}
                data-testid="filter-active"
              >
                {t('timesheet_filter_employees_active_button')}
              </Button>
              <Button
                variant={filter === 'inactive' ? 'default' : 'outline'}
                onClick={() => setFilter('inactive')}
                data-testid="filter-inactive"
              >
                {t('timesheet_filter_employees_inactive_button')}
              </Button>
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                data-testid="filter-all"
              >
                {t('timesheet_filter_employees_all_button')}
              </Button>

              {activeEmployees.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label>{t('timesheet_employee_label')}</Label>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="px-3 py-2 border rounded-md"
                    data-testid="filter-employee-select"
                  >
                    <option value="active">
                      {t('timesheet_employee_option_active')}
                    </option>
                    <optgroup label={t('timesheet_employee_optgroup_active')}>
                      {activeEmployees
                        .filter((emp) => emp && emp.user_id)
                        .map((emp) => (
                          <option key={emp.user_id} value={emp.user_id}>
                            {emp.full_name || 'Óþekktur'}
                          </option>
                        ))}
                    </optgroup>
                    {inactiveEmployees.length > 0 && (
                      <optgroup label={t('timesheet_employee_optgroup_inactive')}>
                        {inactiveEmployees
                          .filter((emp) => emp && emp.user_id)
                          .map((emp) => (
                            <option key={emp.user_id} value={emp.user_id}>
                              {emp.full_name || 'Óþekktur'}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}
            </div>

            {/* Status filter */}
            <div className="mt-4 flex flex-wrap gap-4 items-center">
              <Label className="font-semibold">{t('timesheet_filter_status')}</Label>
              <Button
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('all')}
              >
                {t('timesheet_filter_status_all')}
              </Button>
              <Button
                variant={statusFilter === 'pending' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('pending')}
              >
                {t('timesheet_filter_status_pending')}
              </Button>
              <Button
                variant={statusFilter === 'approved' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('approved')}
              >
                {t('timesheet_filter_status_approved')}
              </Button>
              <Button
                variant={statusFilter === 'rejected' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('rejected')}
              >
                {t('timesheet_filter_status_rejected')}
              </Button>
            </div>

            {/* Date Range Filter */}
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 items-center">
              <Label className="font-semibold">{t('timesheet_period_label')}</Label>
              <Button
                variant={isPeriodActive(currentPeriod()) ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDateFilter(currentPeriod())}
                data-testid="period-current-btn"
              >
                {t('timesheet_period_current')}
              </Button>
              <Button
                variant={isPeriodActive(previousPeriod()) ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDateFilter(previousPeriod())}
                data-testid="period-previous-btn"
              >
                {t('timesheet_period_previous')}
              </Button>
              <div className="flex items-center gap-2">
                <Label className="text-sm">{t('timesheet_period_from_label')}</Label>
                <Input
                  type="date"
                  value={dateFilter.from || ''}
                  onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })}
                  className="w-40"
                  data-testid="date-filter-from"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">{t('timesheet_period_to_label')}</Label>
                <Input
                  type="date"
                  value={dateFilter.to || ''}
                  onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })}
                  className="w-40"
                  data-testid="date-filter-to"
                />
              </div>
              {(dateFilter.from || dateFilter.to) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDateFilter({ from: null, to: null })}
                  data-testid="clear-date-filter"
                >
                  {t('timesheet_period_clear')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {user.role === 'admin' && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600">
                {t('timesheet_selected_count')}: {selectedEntries.length}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const allIds = filteredEntries
                    .filter((e) => e && e.id && e.status !== 'approved')
                    .map((e) => e.id);
                  setSelectedEntries((prev) => prev.length === allIds.length ? [] : allIds);
                }}
                data-testid="select-all-button"
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                {selectedEntries.length === filteredEntries.filter((e) => e?.status !== 'approved').length
                  ? 'Afvelja allar'
                  : 'Velja allar'}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedEntries.length === 0}
              onClick={handleApproveSelected}
              data-testid="approve-selected-button"
            >
              {t('timesheet_approve_selected')} ({selectedEntries.length})
            </Button>
          </div>
        )}

        {/* Time entries */}
        {/* Veikindadagar yfirlit */}
        {user.role === 'admin' && (() => {
          const filteredSick = sickLeaves.filter((req) => {
            const start = new Date(req.start_date);
            const end = new Date(req.end_date);
            const from = dateFilter.from ? new Date(dateFilter.from) : null;
            const to = dateFilter.to ? new Date(dateFilter.to) : null;
            if (from && end < from) return false;
            if (to && start > to) return false;
            if (filter !== 'active' && filter !== 'all' && filter !== 'inactive' && filter !== req.user_id) return false;
            return true;
          });
          if (filteredSick.length === 0) return null;

          // Build per-employee sick day rows within date range (greitt/ógreitt frá backend)
          const sickRows = [];
          filteredSick.forEach((req) => {
            const from = dateFilter.from ? new Date(dateFilter.from) : null;
            const to = dateFilter.to ? new Date(dateFilter.to) : null;
            const pushRow = (ds, paid) => {
              const d = new Date(ds + 'T00:00:00');
              if (from && d < from) return;
              if (to && d > to) return;
              sickRows.push({
                date: d,
                user_id: req.user_id,
                name: getEmployeeName(req.user_id),
                paid,
                hours: req.sick_day_hours,
              });
            };
            if (req.paid_dates || req.unpaid_dates) {
              (req.paid_dates || []).forEach((ds) => pushRow(ds, true));
              (req.unpaid_dates || []).forEach((ds) => pushRow(ds, false));
            } else {
              const start = new Date(req.start_date);
              const end = new Date(req.end_date);
              const current = new Date(Math.max(start, from || start));
              const limit = new Date(Math.min(end, to || end));
              while (current <= limit) {
                sickRows.push({
                  date: new Date(current),
                  user_id: req.user_id,
                  name: getEmployeeName(req.user_id),
                  paid: true,
                  hours: req.sick_day_hours,
                });
                current.setDate(current.getDate() + 1);
              }
            }
          });
          sickRows.sort((a, b) => a.date - b.date);

          return (
            <Card className="bg-orange-50 border-orange-200" data-testid="sick-leave-section">
              <button
                onClick={() => setSickOpen(!sickOpen)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
                data-testid="sick-leave-toggle"
              >
                <span className="text-lg font-semibold flex items-center gap-2">
                  <Thermometer className="h-5 w-5 text-orange-600" />
                  Veikindadagar ({sickRows.length})
                </span>
                <ChevronDown className={`h-5 w-5 text-orange-600 transition-transform ${sickOpen ? 'rotate-180' : ''}`} />
              </button>
              {sickOpen && (
                <CardContent className="pt-0">
                  <div className="divide-y divide-orange-200">
                    {sickRows.map((row, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2" data-testid={`sick-row-${idx}`}>
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{row.name}</span>
                          <span className="text-sm text-gray-500">
                            {row.date.toLocaleDateString('is-IS')}
                          </span>
                        </div>
                        <span className={`text-sm font-semibold ${row.paid ? 'text-orange-700' : 'text-gray-500'}`}>
                          {row.paid
                            ? `Veikindi – ${(row.hours ?? 8).toLocaleString('is-IS')} klst`
                            : 'Ógreidd veikindi – 0 klst'}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })()}

        <div className="grid gap-4">
          {filteredEntries.map((entry) => {
            if (!entry || !entry.id) return null;

            const isActive = getEmployeeStatus(entry.user_id);
            const isSelected = selectedEntries.includes(entry.id);
            return (
              <Card
                key={entry.id}
                className={`shadow-md ${!isActive ? 'opacity-60 border-red-200' : ''}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {user.role === 'admin' && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(entry.id)}
                          className="h-4 w-4"
                          data-testid={`select-entry-${entry.id}`}
                        />
                      )}
                      <CardTitle className="flex items-center">
                        <Clock className="mr-2 h-5 w-5" />
                        {getEmployeeName(entry.user_id)} -{' '}
                        {new Date(entry.clock_in).toLocaleDateString('is-IS', { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' })}
                        {!isActive && (
                          <span className="ml-2 text-sm text-red-500 font-normal">
                            (Óvirkur)
                          </span>
                        )}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.status && (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.status === 'approved'
                              ? 'bg-green-100 text-green-700'
                              : entry.status === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {entry.status === 'approved'
                            ? t('timesheet_status_approved')
                            : entry.status === 'rejected'
                            ? t('timesheet_status_rejected')
                            : t('timesheet_status_pending')}
                        </span>
                      )}
                      {user.role === 'admin' && entry.status !== 'approved' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              await axios.post(
                                `${API}/time/entries/${entry.id}/status`,
                                { status: 'approved' },
                                { headers: { Authorization: `Bearer ${token}` } },
                              );
                              toast.success('Tímaskráning samþykkt');
                              fetchEntries();
                            } catch (error) {
                              console.error('Approve entry error:', error);
                              toast.error('Tókst ekki að samþykkja tímaskráningu');
                            }
                          }}
                          data-testid={`approve-entry-${entry.id}`}
                        >
                          {t('timesheet_approve_single')}
                        </Button>
                      )}
                      <Button
                        onClick={() => handleToggleMap(entry)}
                        variant="outline"
                        size="sm"
                        disabled={!(entry.location_lat && entry.location_lng)}
                        className={
                          !(entry.location_lat && entry.location_lng)
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }
                        data-testid={`show-map-${entry.id}`}
                      >
                        <MapPin className="h-4 w-4 mr-1" />
                        {openMapEntryId === entry.id
                          ? t('timesheet_hide_map')
                          : t('timesheet_show_map')}
                      </Button>
                      {(entry.photo_url || entry.photo_url_out) && (
                        <Button
                          onClick={() => setPhotoModalUrl({ in: fixPhotoUrl(entry.photo_url), out: fixPhotoUrl(entry.photo_url_out) })}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50"
                          data-testid={`show-photo-${entry.id}`}
                        >
                          <Camera className="h-4 w-4 mr-1" />
                          Mynd
                        </Button>
                      )}
                      {(entry.comment_in || entry.comment_out) && (
                        <Button
                          onClick={() => setCommentEntry(entry)}
                          variant="outline"
                          size="sm"
                          className="bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200"
                          data-testid={`show-comment-${entry.id}`}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Athugasemd
                        </Button>
                      )}
                      {(user.role === 'admin' || entry.status !== 'approved') && (
                        <Button
                          onClick={() => handleEdit(entry)}
                          variant="outline"
                          size="sm"
                          data-testid={`edit-entry-${entry.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {t('btn_edit')}
                        </Button>
                      )}
                      {user.role === 'admin' && (
                        <Button
                          onClick={() => handleDelete(entry.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {t('btn_delete')}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">
                        {t('timesheet_field_clock_in_label')}
                      </p>
                      <p className="font-medium">
                        {new Date(entry.clock_in).toLocaleString('is-IS')}
                      </p>
                      {entry.actual_clock_in && entry.actual_clock_in !== entry.clock_in && (
                        <p className="text-xs text-gray-400" data-testid={`actual-in-${entry.id}`}>
                          Mætti: {new Date(entry.actual_clock_in).toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-500">
                        {t('timesheet_field_clock_out_label')}
                      </p>
                      <p className="font-medium">
                        {entry.clock_out
                          ? new Date(entry.clock_out).toLocaleString('is-IS')
                          : t('timesheet_in_progress_value')}
                      </p>
                      {entry.actual_clock_out && entry.actual_clock_out !== entry.clock_out && (
                        <p className="text-xs text-gray-400" data-testid={`actual-out-${entry.id}`}>
                          Fór: {new Date(entry.actual_clock_out).toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-500">
                        {t('timesheet_field_total_hours_label')}
                      </p>
                      <p className="font-medium">
                        {entry.total_hours ? `${entry.total_hours}h` : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">
                        {t('timesheet_field_location_label')}
                      </p>
                      <p className="font-medium">
                        {entry.location_address
                          ? entry.location_address
                          : entry.location_lat
                          ? `${entry.location_lat.toFixed(4)}, ${entry.location_lng.toFixed(4)}`
                          : t('timesheet_no_location_value')}
                      </p>
                    </div>
                  </div>
                </CardContent>
                {openMapEntryId === entry.id && entry.location_lat && entry.location_lng && (
                  <div
                    className="mt-4 h-64 rounded-lg overflow-hidden border"
                    data-testid={`inline-map-${entry.id}`}
                  >
                    <MapContainer
                      center={[entry.location_lat, entry.location_lng]}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <Marker position={[entry.location_lat, entry.location_lng]}>
                        <Popup>{getEmployeeName(entry.user_id)}</Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                )}
              </Card>
            );
          })}
          {filteredEntries.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                Engar tímaskráningar fundust
              </CardContent>
            </Card>
          )}
        </div>

        {/* New manual entry dialog */}
        <Dialog open={newEntryOpen} onOpenChange={setNewEntryOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('timesheet_dialog_new_title')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateEntry} className="space-y-4">
              {user.role === 'admin' && (
                <div className="space-y-2">
                  <Label>{t('timesheet_dialog_employee_label')}</Label>
                  <select
                    value={newEntryForm.user_id}
                    onChange={(e) =>
                      setNewEntryForm({ ...newEntryForm, user_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    required
                    data-testid="new-entry-employee-select"
                  >
                    <option value="">
                      {t('leave_employee_select_placeholder')}
                    </option>
                    {employees.map((emp) => (
                      <option key={emp.user_id} value={emp.user_id}>
                        {emp.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Dagsetning</Label>
                <Input
                  type="date"
                  value={newEntryForm.date}
                  onChange={(e) => setNewEntryForm({ ...newEntryForm, date: e.target.value })}
                  required
                  data-testid="new-entry-date"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TimeInput24
                  label="Inn (24h)"
                  value={newEntryForm.time_in}
                  onChange={(v) => setNewEntryForm({ ...newEntryForm, time_in: v })}
                  required
                  testId="new-entry-time-in"
                />
                <TimeInput24
                  label="Út (24h)"
                  value={newEntryForm.time_out}
                  onChange={(v) => setNewEntryForm({ ...newEntryForm, time_out: v })}
                  testId="new-entry-time-out"
                />
              </div>
              <DurationHint date={newEntryForm.date} timeIn={newEntryForm.time_in} timeOut={newEntryForm.time_out} />
              <p className="text-xs text-gray-500">
                Sláðu inn t.d. „8" → 08:00 eða „1630" → 16:30. Út-tími má vera tómur ef enn í vinnu.
              </p>
              <Button type="submit" className="w-full">
                {t('btn_save')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('timesheet_dialog_edit_title')}</DialogTitle>
            </DialogHeader>
            {editEntry && (
              <form onSubmit={handleUpdateEntry} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('timesheet_dialog_employee_label')}</Label>
                  <Input
                    value={getEmployeeName(editEntry.user_id)}
                    disabled
                    className="bg-gray-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dagsetning</Label>
                  <Input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    required
                    data-testid="edit-date"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <TimeInput24
                    label="Inn (24h)"
                    value={editForm.time_in}
                    onChange={(v) => setEditForm({ ...editForm, time_in: v })}
                    required
                    testId="edit-clock-in"
                  />
                  <TimeInput24
                    label="Út (24h)"
                    value={editForm.time_out}
                    onChange={(v) => setEditForm({ ...editForm, time_out: v })}
                    testId="edit-clock-out"
                  />
                </div>
                <DurationHint date={editForm.date} timeIn={editForm.time_in} timeOut={editForm.time_out} />
                <p className="text-xs text-gray-500">
                  Sláðu inn t.d. „8" → 08:00 eða „1630" → 16:30. Ef út-tími er fyrr en inn-tími telst hann daginn eftir.
                </p>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" data-testid="save-edit-button">
                    {t('btn_save')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditOpen(false)}
                    data-testid="cancel-edit-button"
                  >
                    {t('btn_cancel')}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
        {/* Photo Modal */}
        <Dialog open={!!photoModalUrl} onOpenChange={() => setPhotoModalUrl(null)}>
          <DialogContent className="max-w-lg" aria-describedby="photo-modal-desc">
            <DialogHeader>
              <DialogTitle>Stimpilmyndir</DialogTitle>
            </DialogHeader>
            <p id="photo-modal-desc" className="sr-only">Myndir teknar við stimplun</p>
            {photoModalUrl && (
              <div className="space-y-4">
                {photoModalUrl.in && (
                  <div>
                    <p className="text-sm font-semibold text-green-700 mb-1">Innstimplun</p>
                    <img src={`${BACKEND_URL}${photoModalUrl.in}`} alt="Innstimplun" className="w-full rounded-lg" data-testid="photo-modal-in" />
                  </div>
                )}
                {photoModalUrl.out && (
                  <div>
                    <p className="text-sm font-semibold text-red-700 mb-1">Útstimplun</p>
                    <img src={`${BACKEND_URL}${photoModalUrl.out}`} alt="Útstimplun" className="w-full rounded-lg" data-testid="photo-modal-out" />
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Generate Review Link Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent aria-describedby="link-dialog-desc">
            <DialogHeader>
              <DialogTitle>Búa til review link</DialogTitle>
            </DialogHeader>
            <p id="link-dialog-desc" className="sr-only">Veldu starfsmann og búðu til link</p>
            {!generatedLink ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Starfsmaður</Label>
                  <select
                    value={linkEmployee}
                    onChange={(e) => setLinkEmployee(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="link-employee-select"
                  >
                    <option value="">Veldu starfsmann...</option>
                    {employees.map((emp) => (
                      <option key={emp.user_id} value={emp.user_id}>
                        {emp.full_name}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-sm text-gray-500">
                  Linkurinn gildir í 5 daga.
                  {dateFilter.from && dateFilter.to && (
                    <> Tímabil: {dateFilter.from} til {dateFilter.to}</>
                  )}
                </p>
                <Button onClick={handleGenerateLink} className="w-full" data-testid="generate-link-confirm">
                  Búa til link
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm">Link fyrir <strong>{generatedLink.name}</strong>:</p>
                <div className="flex gap-2">
                  <Input
                    value={generatedLink.url}
                    readOnly
                    className="text-xs"
                    data-testid="generated-link-input"
                  />
                  <Button onClick={copyLink} variant="outline" data-testid="copy-link-button">
                    Afrita
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Gildir til {new Date(generatedLink.expires).toLocaleDateString('is-IS')}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Comment Modal */}
        <Dialog open={!!commentEntry} onOpenChange={() => setCommentEntry(null)}>
          <DialogContent aria-describedby="comment-modal-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-yellow-600" />
                Athugasemd
              </DialogTitle>
            </DialogHeader>
            <p id="comment-modal-desc" className="sr-only">Athugasemdir starfsmanns</p>
            {commentEntry && (
              <div className="space-y-3">
                {commentEntry.comment_in && (
                  <div className="p-3 bg-green-50 rounded-lg" data-testid="comment-in-display">
                    <p className="text-xs font-semibold text-green-700 mb-1">Innstimplun:</p>
                    <p className="text-sm">{commentEntry.comment_in}</p>
                  </div>
                )}
                {commentEntry.comment_out && (
                  <div className="p-3 bg-red-50 rounded-lg" data-testid="comment-out-display">
                    <p className="text-xs font-semibold text-red-700 mb-1">Útstimplun:</p>
                    <p className="text-sm">{commentEntry.comment_out}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
